import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  isParentIdInEnabledList,
  enabledParentIdsState,
  getEnabledFilteredDropDownOptionsFromComponent,
} from "atoms/fnolInstanceState.atom";
import {
  filteredDropDownOptionsState,
  removeOldFilteredDropdownOptions,
} from "atoms/filteredDropDownOptionsState.atom";
import {
  getFnolInstanceWithThirdPartyState,
  getFnolInstanceMetaDataState,
  updateOverrideFNOLDataState,
  updateFNOLInstanceIsDirtyState,
  getFNOLInstanceIsDirtyState,
  pushParentKeyToParentControlArrayState,
  getFnolQuestionSetStructure,
} from "store/UpdateFnolInstanceState";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { FNOLCOMPONENTLISTDETAILS } from "constants/FnolComponentConstants";
import * as componentConstants from "constants/FnolComponentConstants";
import {
  isNullOrEmpty,
  isNullOrEmptyOrZero,
  isNullOrLengthIsZero,
  sortArrayOptionsAlphabetically,
} from "shared/Helpers";
import { createFnolInstance } from "api-calls/SaveFnolInstance";
import styles from "components/FnolViewerComponentWrapper.module.css";
import { schemeIdState } from "atoms/schemeIdState.atom";
import { isEqual } from "lodash";
import * as FieldMappingConstants from "constants/FieldMappingConstants";
import { Stack } from "@mui/material";
import InfoIcon from "./InfoIcon";
import * as fnolproperitesConstants from "constants/FnolPropertiesConstants";
import * as fnolOnPropertyChangeConstants from "constants/FnolOnPropertyChangeConstants";
import * as fnolComponentConstants from "constants/FnolComponentConstants";
import * as visibilityConstants from "constants/VisibilityConstants";
import { generateOnPropertyChangeParameters } from "shared/OnPropertyChangeObjects";
import Fade from "@mui/material/Fade";
import { useTheme } from "@emotion/react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { updateFnolInstanceComponent } from "slices/fnolInstanceSlice";
import { fireOnPropertyChange, resetOnPropertyChangeDataForField } from "slices/fnolOnPropertyChangeSlice";

const FnolViewerComponentWrapper = (props) => {
  const UIFnolComponent = props.uiFnolComponent;
  const [fnolComponent, setFnolComponent] = useState({ ...props.fnolComponent });
  const isEnabled = useRecoilValue(isParentIdInEnabledList(fnolComponent.parentLinkIdsArray));
  const schemeId = useRecoilValue(schemeIdState);
  const retrievedComponent = useSelector(
    (state) => state.fnolInstance.componentValues.byFieldMappingId[fnolComponent.fieldMappingId ?? ""] ?? null,
    shallowEqual
  );
  const retrievedComponentValue = retrievedComponent?.value ?? null;
  const componentAutoPopulatedBy = retrievedComponent?.autoPopulatedBy ?? null;
  const onPropertyChange =
    useSelector(
      (state) =>
        state.fnolOnPropertyChangeData.autoPopulationData.standard.byFieldPath[fnolComponent.fieldMapping ?? ""],
      shallowEqual
    ) ?? null;
  const [hasPropertyOnChanged] = useState(fnolComponent?.hasOnPropertyChanged);
  const setParentIdsState = useSetRecoilState(enabledParentIdsState);
  const setFilteredDropDownOptionsState = useSetRecoilState(filteredDropDownOptionsState);
  const previousComponentValue = useRef(retrievedComponentValue);
  const [triggerChainedOnPropertyChange, setTriggerChainedOnPropertyChange] = useState(false);
  const filteredDropdowns = useRecoilValue(getEnabledFilteredDropDownOptionsFromComponent(fnolComponent));
  const theme = useTheme();
  const [isHidden, setIsHidden] = useState(false);
  const [visibilityToggled, setVisibilityToggled] = useState(true);
  const dispatch = useDispatch();

  const onFieldChangeHandler = async (
    fieldValue,
    props,
    dropdownId,
    isOnBlurChange,
    triggeredByOnPropertyChange = false,
    triggeredBy = null,
    isResetChange = false
  ) => {
    // Perform a check to see if fieldValue is different from what's already stored
    // this stops the on property change inifinite loop issue
    const areComponentValuesEqual = isEqual(retrievedComponentValue, fieldValue);

    if (areComponentValuesEqual === false) {
      dispatch(
        updateFnolInstanceComponent({
          fieldValue: fieldValue,
          props: props,
          dropdownId: dropdownId,
          autoPopulatedBy: triggeredBy,
        })
      );

      const isFNOLInstanceDirty = getFNOLInstanceIsDirtyState();
      if (isFNOLInstanceDirty === false) {
        updateFNOLInstanceIsDirtyState(true);
      }
      if (triggeredByOnPropertyChange) {
        const wasValueAutoPopulated = isResetChange ? false : true;
        setFnolComponent((prev) => ({
          ...prev,
          wasValuePopulatedBySystem: wasValueAutoPopulated,
        }));
        previousComponentValue.current = retrievedComponentValue;
        setTriggerChainedOnPropertyChange(true);
        if (shouldHideField(wasValueAutoPopulated)) {
          setIsHidden(true);
        } else if (shouldUnHideField(wasValueAutoPopulated)) {
          setIsHidden(false);
        }
        return;
      } else {
        setFnolComponent((prev) => ({
          ...prev,
          wasValuePopulatedBySystem: false,
        }));
        if (shouldUnHideField(false)) {
          setIsHidden(false);
        }
      }

      if (shouldTriggerOnPropertyChange(fieldValue)) {
        const saveInstance = false;
        buildFnolInstance(saveInstance).then((fnolInstance) => {
          const onPropertyChangeParameters = generateOnPropertyChangeParameters(
            fnolComponent,
            fnolInstance,
            retrievedComponentValue
          );
          if (onPropertyChangeParameters?.length > 0) {
            dispatch(fireOnPropertyChange(...onPropertyChangeParameters));
          }
        });
      }
    }
  };

  const onDropdownFilterFieldChangeHandler = async (
    filteredDropdownOptions,
    isFilteredChildDropdownOptionsFromFNOL = false
  ) => {
    if (filteredDropdownOptions.length !== 0) {
      let updatedAllFilteredDropdownOptions = removeOldFilteredDropdownOptions(
        filteredDropdownOptions,
        isFilteredChildDropdownOptionsFromFNOL
      );
      let newAllFilteredDropdownOptions = mergeUniqueDropdowns(
        updatedAllFilteredDropdownOptions,
        filteredDropdownOptions
      );
      setFilteredDropDownOptionsState(sortArrayOptionsAlphabetically(newAllFilteredDropdownOptions, "value"));
    }
  };

  // Handles visibility changes within the component itself, independent of prop visibility status
  function onVisibilityToggled(isVisible) {
    setVisibilityToggled(isVisible);
  }

  const mergeUniqueDropdowns = (updatedFilteredDropdowns, filteredDropdownOptions) => {
    let optionsMap = new Map();

    updatedFilteredDropdowns.forEach((option) => optionsMap.set(option.key, option));
    filteredDropdownOptions.forEach((option) => optionsMap.set(option.id, option));

    return Array.from(optionsMap.values());
  };

  useEffect(() => {
    // Sets default value to fields without overwriting if a value has previously been set
    if (retrievedComponentValue === null && fnolComponent.defaultValue) {
      const fnolDropDownValue =
        fnolComponent.dropDownOptions && fnolComponent.dropDownOptions.length > 0
          ? fnolComponent.dropDownOptions.find((d) => d.name === fnolComponent.defaultValue)
          : null;

      const dropdownId = fnolDropDownValue ? fnolDropDownValue.id : null;

      onFieldChangeHandler(fnolComponent.defaultValue, props.fnolComponent, dropdownId, false);
    }
  }, [retrievedComponentValue]);

  useEffect(() => {
    // Note they want hidden if system autopoulated fields to show on loading a saved fnol, even if populated.
    // Thus only always hidden fields are hidden on fnol load.
    if (fnolComponent.visibility === visibilityConstants.ALWAYS_HIDDEN) {
      setIsHidden(true);
    }
  }, []);

  // Handle async visibility check for "reason for updated fnol" field
  useEffect(() => {
    if (fnolComponent?.fieldMapping?.toLowerCase() === FieldMappingConstants.ReasonForUpdatedFnol.toLowerCase()) {
      getFnolInstanceMetaDataState().then((fnolInstanceMetaDataState) => {
        if (!fnolInstanceMetaDataState.isSavedAndSent) {
          setIsHidden(true);
        } else {
          setIsHidden(false);
        }
      });
    }
  }, [fnolComponent.fieldMapping]);

  // [First Render Only] - Update existing fnol components, so that component order is added
  useEffect(() => {
    if (!isNullOrEmpty(retrievedComponentValue) && isNullOrEmpty(fnolComponent.componentOrder)) {
      dispatch(
        updateFnolInstanceComponent({
          fieldValue: retrievedComponentValue,
          props: fnolComponent,
          dropdownId: "",
          autoPopulatedBy: componentAutoPopulatedBy ?? null,
        })
      );
    }
  }, []);

  // [First Render Only] - Works out if this is a parent and is 'activated' when viewing a previously saved fnol
  useEffect(() => {
    if (!isNullOrEmpty(retrievedComponentValue) && fnolComponent.isParentControl) {
      if (fnolComponent.controlType === componentConstants.FNOLCOMPONENT_CHECKBOX) {
        pushParentKeyToParentControlArrayState(fnolComponent.componentKey);
      } else if (fnolComponent.controlType === componentConstants.FNOLCOMPONENT_RADIOBUTTON) {
        pushParentKeyToParentControlArrayState(fnolComponent.componentKey + ":" + retrievedComponentValue);
      } else if (!isNullOrEmpty(fnolComponent?.dropDownOptions) && fnolComponent.dropDownOptions?.length > 0) {
        const dropDownOption = fnolComponent.dropDownOptions.find((d) => d.name === retrievedComponentValue);
        if (!isNullOrEmpty(dropDownOption)) {
          pushParentKeyToParentControlArrayState(fnolComponent.componentKey + ":" + dropDownOption.id);
        }
      } else if (
        !isNullOrEmpty(fnolComponent?.savedDropDownOptions) &&
        fnolComponent.savedDropDownOptions?.length > 0
      ) {
        const savedDropDownOption = fnolComponent.savedDropDownOptions.find((d) => d.value === retrievedComponentValue);
        if (!isNullOrEmpty(savedDropDownOption)) {
          pushParentKeyToParentControlArrayState(fnolComponent.componentKey + ":" + savedDropDownOption.id);
        }
      }
    }
  }, []);

  useEffect(() => {
    // for multiDropdowns the onPropertyChange is passed to and processed inside the component
    if (
      onPropertyChange === null ||
      fnolComponent.controlType === componentConstants.FNOLCOMPONENT_MULTIOPTIONDROPDOWN
    ) {
      return;
    }

    if (onPropertyChange.value === fnolOnPropertyChangeConstants.ResetCode) {
      onFieldChangeHandler(null, fnolComponent, "", false, true, null, true);
    } else if (!isNullOrEmpty(onPropertyChange.value)) {
      onFieldChangeHandler(
        onPropertyChange.value,
        fnolComponent,
        "",
        false,
        true,
        onPropertyChange.triggeredByFieldPath
      );
    }
    dispatch(resetOnPropertyChangeDataForField({ fieldPath: onPropertyChange.fieldPath }));
  }, [onPropertyChange]);

  // Triggers onPropertyChanges of a field, once the fnol instance contains the up-to-date field value
  useEffect(() => {
    if (triggerChainedOnPropertyChange) {
      let fieldValue = previousComponentValue.current;
      if (shouldTriggerOnPropertyChange(fieldValue)) {
        previousComponentValue.current = retrievedComponentValue;
        const saveInstance = false;
        buildFnolInstance(saveInstance).then((fnolInstance) => {
          const onPropertyChangeParameters = generateOnPropertyChangeParameters(
            fnolComponent,
            fnolInstance,
            fieldValue
          );
          if (onPropertyChangeParameters?.length > 0) {
            dispatch(fireOnPropertyChange(...onPropertyChangeParameters));
          }
        });
      }
      setTriggerChainedOnPropertyChange(false);
    }
  }, [triggerChainedOnPropertyChange]);

  useEffect(() => {
    if (
      isEnabled &&
      !isNullOrLengthIsZero(filteredDropdowns) &&
      !isNullOrLengthIsZero(
        fnolComponent.filteredChildDropdownOptions && !isNullOrEmptyOrZero(fnolComponent.parentLinkIdsArray)
      )
    ) {
      onDropdownFilterFieldChangeHandler(filteredDropdowns, true);
    }
  }, [filteredDropdowns]);

  const createFieldRecord = (value, fnolComponent, autoPopulatedBy = null) => {
    return {
      componentOrder: fnolComponent.componentOrder,
      controlType: fnolComponent.controlType,
      onScreenText: fnolComponent.onScreenText,
      fieldMapping: fnolComponent.fieldMapping || "[Unknown]",
      fieldMappingId: fnolComponent.fieldMappingId,
      value: value,
      [componentConstants.AUTO_POPULATED_BY]: autoPopulatedBy,
    };
  };

  const createAdditionalItems = (fnolInstanceMetaDataState, props) => {
    if (isNullOrEmpty(fnolInstanceMetaDataState._id)) {
      return { userId: props.userId };
    } else {
      return {
        _id: fnolInstanceMetaDataState._id,
        userId: fnolInstanceMetaDataState.userId,
        jobId: fnolInstanceMetaDataState.jobId,
        createdOn: fnolInstanceMetaDataState.createdOn,
        firstModifiedBy: fnolInstanceMetaDataState.firstModifiedBy || null,
        firstModifiedDate: fnolInstanceMetaDataState.firstModifiedDate || null,
        lastModifiedBy: props.userId,
        lastModifiedDate: fnolInstanceMetaDataState.lastModifiedDate || null,
        reportMethod: fnolInstanceMetaDataState.reportMethod,
        fnolVersion: fnolInstanceMetaDataState.fnolVersion ?? null,
        isSavedAndSent: fnolInstanceMetaDataState.isSavedAndSent ?? null,
        isUpdate: fnolInstanceMetaDataState.isUpdate,
      };
    }
  };

  const removeOldParentKeyFromParentControlArrayState = (oldDropdownKey) => {
    setParentIdsState((prevParentIds) => {
      // Clone the array to avoid mutating the original array
      const enabledArrayParentIds = [...prevParentIds];

      // Check if the old dropdown key is in the list and remove it
      const controlIndex = enabledArrayParentIds.indexOf(oldDropdownKey);
      if (controlIndex !== -1) {
        enabledArrayParentIds.splice(controlIndex, 1);
      }

      return enabledArrayParentIds;
    });
  };

  const updateParentControlArrayState = (parentComponentKey, parentIsSelected, oldDropdownKey) => {
    setParentIdsState((prevParentIds) => {
      // Clone the array to avoid mutating the original array
      const enabledArrayParentIds = [...prevParentIds];

      // Check scenarios where there are no changes so does not need a rerender of ids
      if (
        (parentIsSelected && enabledArrayParentIds.includes(parentComponentKey)) ||
        (parentIsSelected === null && !enabledArrayParentIds.includes(parentComponentKey))
      ) {
        return prevParentIds;
      }

      // Remove the parent ID key regardless if it's in the list
      const controlIndex = enabledArrayParentIds.indexOf(parentComponentKey);
      if (controlIndex !== -1) {
        enabledArrayParentIds.splice(controlIndex, 1);
      }

      // Remove the old dropdown key that was changed
      if (oldDropdownKey !== null) {
        const oldControlIndex = enabledArrayParentIds.indexOf(oldDropdownKey);
        if (oldControlIndex !== -1) {
          enabledArrayParentIds.splice(oldControlIndex, 1);
        }
      }

      // Add the new parent Id that is selected for the children to show
      if (parentIsSelected) {
        enabledArrayParentIds.push(parentComponentKey);
      }

      return enabledArrayParentIds;
    });
  };

  const buildFnolInstance = (saveInstance, value = null, fnolComponent = null) => {
    return new Promise((resolve) => {
      getFnolInstanceMetaDataState().then((fnolInstanceMetaDataState) => {
        getFnolInstanceWithThirdPartyState().then(async (fnolInstanceState) => {
          let fnolInstance = null;
          let fieldRecord = null;

          if (fnolInstanceState.length === 0 && value !== null && fnolComponent !== null) {
            fieldRecord = createFieldRecord(value, fnolComponent);
            fnolInstanceState.push(fieldRecord);
          }

          let fnolSchemeId = " ";
          if (isNullOrEmpty(schemeId)) {
            if (!isNullOrEmpty(fnolInstanceMetaDataState._id) && !isNullOrEmpty(fnolInstanceMetaDataState.schemeId)) {
              fnolSchemeId = fnolInstanceMetaDataState.schemeId;
            }
          } else {
            fnolSchemeId = schemeId;
          }

          const questionSetStructure = await getFnolQuestionSetStructure();

          fnolInstance = {
            customerId: props.customerId,
            tempDocumentsId: questionSetStructure.tempDocumentsId,
            fnolQuestionSetId: props.fnolQuestionSetId,
            fnolValues: fnolInstanceState,
            schemeId: fnolSchemeId,
          };

          if (isNullOrEmpty(fnolInstanceMetaDataState._id)) {
            const additionalItems = createAdditionalItems(fnolInstanceMetaDataState, props);
            Object.assign(fnolInstance, additionalItems);
            if (saveInstance) {
              createFnolInstance(fnolInstance);
            }
          }
          resolve(fnolInstance);
        });
      });
    });
  };

  const shouldHideField = (wasValueAutoPopulated) => {
    return (
      fnolComponent.visibility !== undefined &&
      (fnolComponent.visibility === visibilityConstants.ALWAYS_HIDDEN ||
        (fnolComponent.visibility === visibilityConstants.HIDDEN_IF_SYSTEM_POPULATED && wasValueAutoPopulated))
    );
  };

  const shouldUnHideField = (wasValueAutoPopulated) => {
    return fnolComponent.visibility === visibilityConstants.HIDDEN_IF_SYSTEM_POPULATED && !wasValueAutoPopulated;
  };

  const shouldTriggerOnPropertyChange = (fieldValue) =>
    hasPropertyOnChanged &&
    ((fnolComponent.controlType === componentConstants.FNOLCOMPONENT_ADDRESSFORM &&
      (retrievedComponentValue === null ||
        (fieldValue?.postcode !== undefined && fieldValue.postcode !== retrievedComponentValue.postcode))) ||
      fnolComponent.controlType !== componentConstants.FNOLCOMPONENT_ADDRESSFORM);

  const saveFnolInstanceHandler = () => {
    return new Promise((resolve) => {
      const saveInstance = true;
      buildFnolInstance(saveInstance).then((fnolInstance) => {
        resolve(true);
      });
    });
  };

  const renderFnolComponent = (fnolComponent) => {
    const controlIndex = FNOLCOMPONENTLISTDETAILS.findIndex((c) => c.key === fnolComponent.controlType);
    if (controlIndex !== -1) {
      const componentChildIsHidden =
        isNullOrLengthIsZero(fnolComponent.parentLinkIdsArray) ||
        (!isNullOrLengthIsZero(fnolComponent.parentLinkIdsArray) && isEnabled)
          ? false
          : true;
      const componentChildIsDisabled = componentChildIsHidden && fnolComponent.showChildControlAsDisabled;
      let componentStyle = !componentChildIsDisabled ? styles.enabled : styles.disabled;
      componentStyle = componentChildIsHidden && !componentChildIsDisabled ? styles.hidden : componentStyle;

      return (
        <Suspense fallback={<div></div>}>
          <Fade in={true} timeout={2000}>
            <div className={componentStyle}>
              <Stack direction="row">
                <div style={{ flexBasis: "95%" }}>
                  <UIFnolComponent
                    key={fnolComponent.componentKey}
                    customerId={props.customerId}
                    data={fnolComponent}
                    fnolQuestionSetId={props.fnolQuestionSetId}
                    isMandatory={!componentChildIsHidden & fnolComponent.isMandatory}
                    onParentSelected={(componentKey, fnolValue, oldDropdownKey = null) =>
                      updateParentControlArrayState(componentKey, fnolValue, oldDropdownKey)
                    }
                    onParentRemoval={(oldDropdownKey) => {
                      removeOldParentKeyFromParentControlArrayState(oldDropdownKey);
                    }}
                    onFieldChange={(
                      event,
                      fieldValue,
                      props,
                      dropdownId = "",
                      isOnBlurChange = false,
                      triggeredByOnPropertyChange = false,
                      triggeredBy = null,
                      isResetChange = false
                    ) => {
                      onFieldChangeHandler(
                        fieldValue,
                        props,
                        dropdownId,
                        isOnBlurChange,
                        triggeredByOnPropertyChange,
                        triggeredBy,
                        isResetChange
                      );
                    }}
                    saveAndSendFNOLHandler={saveFnolInstanceHandler}
                    value={retrievedComponentValue}
                    onPropertyChange={onPropertyChange ?? null}
                    autoPopulatedBy={componentAutoPopulatedBy}
                    autoPopulationOptionsHidden={props.autoPopulationOptionsHidden}
                    isHidden={isHidden}
                    onDropdownFilterFieldChange={(event, filteredDropdownOptions) => {
                      onDropdownFilterFieldChangeHandler(filteredDropdownOptions);
                    }}
                    onVisibilityToggled={(isVisible) => {
                      onVisibilityToggled(isVisible);
                    }}
                  />
                </div>
                {fnolComponent[fnolproperitesConstants.ShowInfoIcon] && !isHidden && visibilityToggled && (
                  <div
                    style={{
                      flexBasis: "5%",
                      display: "flex",
                      alignItems: fnolComponentConstants.blockComponentTypes.includes(fnolComponent.controlType)
                        ? "start"
                        : "center",
                    }}
                  >
                    <InfoIcon
                      fillColour={theme.palette.primary.main}
                      text={fnolComponent[fnolproperitesConstants.InfoIconText] ?? ""}
                    />
                  </div>
                )}
              </Stack>
            </div>
          </Fade>
        </Suspense>
      );
    }
  };

  return <div>{renderFnolComponent(fnolComponent)}</div>;
};

export default FnolViewerComponentWrapper;
